import { useEffect, useState } from 'preact/hooks';
import { api, signOut } from '../api.ts';
import { bump } from '../signal.ts';
import { show, showError } from '../toast.ts';
import { fromKg, LB, toKg, weightUnit } from '../format.ts';
import type { Settings, StravaStatus, User } from '../../shared/types.ts';

export function SettingsView({ settings, user, params, onSaved }: {
  settings: Settings;
  user: User;
  params: URLSearchParams;
  onSaved: (s: Settings) => void;
}) {
  const [s, setS] = useState(settings);
  useEffect(() => setS(settings), [settings]);

  useEffect(() => {
    if (params.get('strava') === 'connected') show('Strava connected; syncing activities');
    const err = params.get('strava_error');
    if (err) showError(err);
    if (params.size) history.replaceState(null, '', '#settings');
  }, []);

  async function save(patch: Partial<Settings>) {
    const next = { ...s, ...patch };
    setS(next);
    try {
      onSaved(await api<Settings>('/api/settings', { method: 'PUT', body: patch }));
      bump();
    } catch (e) {
      showError(e);
      setS(settings);
    }
  }

  const unit = weightUnit(s);
  const rate = fromKg(s.rateKgPerWeek, s);
  const goal = s.rateKgPerWeek < 0 ? 'lose' : s.rateKgPerWeek > 0 ? 'gain' : 'maintain';
  const rateStep = s.units === 'imperial' ? 0.25 : 0.1;

  return (
    <div class="settings">
      <section class="card form">
        <h2>Goal</h2>
        <div class="segmented">
          {(['lose', 'maintain', 'gain'] as const).map((g) => (
            <button
              key={g}
              class={goal === g ? 'on' : ''}
              onClick={() => save({ rateKgPerWeek: g === 'maintain' ? 0 : (g === 'lose' ? -1 : 1) * (Math.abs(s.rateKgPerWeek) || 0.25) })}
            >
              {g[0].toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
        {goal !== 'maintain' && (
          <label>
            <span>Rate ({unit} per week)</span>
            <NumberInput
              value={Math.abs(rate)}
              step={rateStep}
              onCommit={(v) => save({ rateKgPerWeek: Math.sign(s.rateKgPerWeek) * Math.min(goal === 'lose' ? 1.5 : 1, toKg(v, s)) })}
            />
            <small class="muted">
              ≈ {Math.round((Math.abs(s.rateKgPerWeek) * 7700) / 7)} kcal/day {goal === 'lose' ? 'deficit' : 'surplus'}
            </small>
          </label>
        )}
        <div class="pair">
          <label>
            <span>Protein (g per {unit})</span>
            <NumberInput
              value={s.units === 'imperial' ? s.proteinGPerKg / LB : s.proteinGPerKg}
              step={0.1}
              onCommit={(v) => save({ proteinGPerKg: s.units === 'imperial' ? v * LB : v })}
            />
          </label>
          <label>
            <span>Fat (% of calories)</span>
            <NumberInput value={Math.round(s.fatPct * 100)} step={1} onCommit={(v) => save({ fatPct: v / 100 })} />
          </label>
        </div>
        <label class="check">
          <input type="checkbox" checked={s.exerciseAdjust} onChange={(e) => save({ exerciseAdjust: e.currentTarget.checked })} />
          <span>
            Eat back exercise
            <small class="muted">
              {s.exerciseAdjust
                ? 'Each day’s target adds that day’s Strava activity: more food on long-ride days, less on rest days.'
                : 'Targets stay flat, using your average daily exercise.'}
            </small>
          </span>
        </label>
      </section>

      <section class="card form">
        <h2>You</h2>
        <div class="segmented">
          {(['male', 'female'] as const).map((x) => (
            <button key={x} class={s.sex === x ? 'on' : ''} onClick={() => save({ sex: x })}>{x === 'male' ? 'Male' : 'Female'}</button>
          ))}
        </div>
        <div class="pair">
          <label><span>Birth year</span><NumberInput value={s.birthYear} step={1} onCommit={(v) => save({ birthYear: v })} /></label>
          <label><span>Height (cm)</span><NumberInput value={s.heightCm} step={1} onCommit={(v) => save({ heightCm: v })} /></label>
        </div>
        <label>
          <span>Starting weight ({unit})</span>
          <NumberInput value={+fromKg(s.startWeightKg, s).toFixed(1)} step={0.1} onCommit={(v) => save({ startWeightKg: toKg(v, s) })} />
          <small class="muted">Used until your first weigh-in.</small>
        </label>
        <div class="segmented">
          <button class={s.units === 'metric' ? 'on' : ''} onClick={() => save({ units: 'metric' })}>kg</button>
          <button class={s.units === 'imperial' ? 'on' : ''} onClick={() => save({ units: 'imperial' })}>lb</button>
        </div>
      </section>

      <StravaCard />

      <section class="card form">
        <p class="muted">Signed in as {user.name} ({user.email})</p>
        <button class="ghost" onClick={() => signOut().catch(showError)}>Sign out</button>
      </section>
    </div>
  );
}

function NumberInput({ value, step, onCommit }: { value: number; step: number; onCommit: (v: number) => void }) {
  const [v, setV] = useState(String(+value.toFixed(2)));
  useEffect(() => setV(String(+value.toFixed(2))), [value]);
  const commit = () => {
    const n = Number(v.replace(',', '.'));
    if (Number.isFinite(n) && n !== +value.toFixed(2)) onCommit(n);
    else setV(String(+value.toFixed(2)));
  };
  return (
    <input
      inputMode="decimal"
      step={step}
      value={v}
      onInput={(e) => setV(e.currentTarget.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
    />
  );
}

function StravaCard() {
  const [st, setSt] = useState<StravaStatus | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api<StravaStatus>('/api/strava').then(setSt, showError);
  }, []);
  if (!st) return null;

  const run = async (path: string) => {
    setBusy(true);
    try {
      setSt(await api<StravaStatus>(path, { body: {} }));
      bump();
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section class="card form">
      <h2>Strava</h2>
      <p class="muted">
        {st.source === 'strava' && `Connected${st.athleteName ? ` as ${st.athleteName}` : ''}. Calories use Strava’s own estimate when it has one.`}
        {st.source === 'trainer' && 'Reading activities that trainer has synced from Strava.'}
        {st.source === null &&
          (st.configured
            ? 'Not connected. Exercise calories won’t be counted until you connect Strava.'
            : 'No activities found for your email. Exercise calories won’t be counted.')}
      </p>
      {st.lastSync && <p class="muted small">Last synced {new Date(st.lastSync).toLocaleString()}</p>}
      {st.lastError && <p class="error small">{st.lastError}</p>}
      <div class="row">
        {st.source && <button class="ghost" disabled={busy} onClick={() => run('/api/strava/sync')}>{busy ? 'Syncing…' : 'Sync now'}</button>}
        {st.configured && !st.connected && (
          <button
            class="primary"
            onClick={async () => {
              try {
                location.href = (await api<{ url: string }>('/api/strava/connect', { body: {} })).url;
              } catch (e) {
                showError(e);
              }
            }}
          >
            Connect Strava
          </button>
        )}
        {st.connected && <button class="danger" disabled={busy} onClick={() => run('/api/strava/disconnect')}>Disconnect</button>}
      </div>
    </section>
  );
}
